import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import styled, { css } from 'styled-components';
import breakpoints from 'utils/breakpoints';
import { colors } from 'utils/tailwindColors';

export const HOME_TYPES = {
  QMI: 'QMI',
  PLAN: 'Plan',
};

// TODO(MTTMY): titles are new copy, no Sitecore fields yet. Once they exist
// they arrive through the rendering item like result.headline. shortLabel is
// for mobile, where the full title does not fit; anchor is both the DOM id
// and the hash that opens the tab.
export const HOME_TYPE_OPTIONS = [
  {
    value: HOME_TYPES.QMI,
    anchor: 'quickmoveins',
    title: 'Quick Move-In Homes',
    shortLabel: 'Quick Move-In',
  },
  {
    value: HOME_TYPES.PLAN,
    anchor: 'floorplans',
    title: 'Floorplans',
    shortLabel: 'Floorplans',
  },
];

/**
 * Maps a URL hash to its tab, or undefined when the hash names none so the
 * caller decides what to do.
 */
export const homeTypeFromHash = (hash) => {
  if (!hash) return undefined;
  const anchor = hash.replace('#', '').toLowerCase();
  const option = HOME_TYPE_OPTIONS.find((o) => o.anchor === anchor);
  return option && option.value;
};

const ACCENT = colors['mattamy-blue'];
const GRAY_TEXT = colors['mattamy-gray-font'];
const GRAY_BG = colors['mattamy-gray-bg'];

// An empty collection never takes the blue - not selected, not on hover -
// but stays clickable so the empty state is reachable.
const accentOf = ({ $empty }) => ($empty ? GRAY_TEXT : ACCENT);

const Badge = styled.div`
  font-variant-numeric: tabular-nums;
  width: 1.5rem;
  height: 1.5rem;
  font-size: 0.8125rem;
  /* Inverts with the tab: white on top of the filled block. */
  background-color: ${({ $selected }) => ($selected ? colors.white : GRAY_BG)};
  color: ${({ $selected, ...rest }) =>
    $selected ? accentOf(rest) : GRAY_TEXT};
  transition: background-color 0.2s ease-out, color 0.2s ease-out;

  @media ${breakpoints.md} {
    width: 1.875rem;
    height: 1.875rem;
    font-size: 1rem;
  }
`;

/**
 * Same mechanics as CommunityProductOverview's old ButtonTab: the active tab
 * is a filled block with rounded top corners, and the 0.25rem line runs under
 * the inactive ones, not the active one, which rests on it like a folder tab.
 *
 * flex: 1 1 0 with a zero basis is what makes the header span the full width:
 * the tabs split it evenly and each label centers in its half, whatever its
 * text measures. min-width: 0 keeps the row inside the screen: it never
 * scrolls, so on a narrow mobile a long label wraps instead of overflowing.
 */
const Tab = styled.button`
  flex: 1 1 0;
  min-width: 0;
  border-radius: 0.75rem 0.75rem 0 0;
  padding: 1rem 0.5rem;
  font-size: 1.25rem;
  line-height: 1;
  transition: background-color 0.2s ease-out, color 0.2s ease-out,
    border-color 0.2s ease-out;
  /* A tap must change the tab on the first try: no double-tap-to-zoom wait,
     no gray flash, and the inner spans never become the tap target. */
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  & * {
    pointer-events: none;
  }

  @media ${breakpoints.md} {
    padding: 1rem;
    font-size: 1.75rem;
  }

  ${({ $selected, ...rest }) =>
    $selected
      ? css`
          background-color: ${accentOf(rest)};
          color: ${colors.white};
        `
      : css`
          /* Inactive: one gray for everything, line included. Nothing hints
             at the active color until the tab is pointed at. */
          border-bottom: 0.25rem solid ${GRAY_TEXT};
          color: ${GRAY_TEXT};

          /* Pointer devices only: on touch, iOS turns the first tap into a
             hover and the tab needs a second one to change. */
          @media (hover: hover) {
            &:hover {
              color: ${accentOf(rest)};
              border-bottom-color: ${accentOf(rest)};

              /* The badge joins the hover: left gray it would read as not
                 part of the control. */
              ${Badge} {
                background-color: ${accentOf(rest)};
                color: ${colors.white};
              }
            }
          }
        `}

  &:focus-visible {
    outline: 0.1875rem solid ${colors.aqua};
    outline-offset: 0.25rem;
  }
`;

// No margins: the Tab centers its content and any margin shifts the text off
// the badge; the padding does the spacing. Optical corrections go through
// transform, not margin, so the box stays put.
const Label = styled.span`
  font-size: inherit;
  line-height: 1;
`;

/**
 * Section header: both collections as tabs splitting the width of the block,
 * with their counts. A radiogroup and not a list of links because the content
 * swaps client side; each tab's id is the anchor that makes a collection
 * shareable.
 */
const HomeTypeSections = ({
  value,
  onChange,
  counts = {},
  isMobile = false,
  className = '',
  ariaLabel = 'Home type',
}) => (
  <div className={cx('w-full', className)}>
    <div
      // No gap: the tabs touch, so the inactive line starts exactly where the
      // active block ends. Separated they read as two loose pieces.
      className='flex items-stretch'
      role='radiogroup'
      aria-label={ariaLabel}
    >
      {HOME_TYPE_OPTIONS.map((option) => {
        const isSelected = option.value === value;
        const count = counts[option.value];
        const isEmpty = count === 0;
        const label = isMobile ? option.shortLabel : option.title;

        return (
          <Tab
            key={option.value}
            id={option.anchor}
            type='button'
            role='radio'
            aria-checked={isSelected}
            aria-label={
              isEmpty
                ? `${option.title}, none available`
                : `Show ${option.title}, ${count} available`
            }
            tabIndex={isSelected ? 0 : -1}
            onClick={() => onChange(option.value)}
            $selected={isSelected}
            $empty={isEmpty}
            className='flex items-center justify-center gap-2 md:gap-4 text-center md:whitespace-nowrap cursor-pointer'
          >
            <Badge
              aria-hidden='true'
              $selected={isSelected}
              $empty={isEmpty}
              className='flex-none flex items-center justify-center rounded-full font-trade-gothic-20 -mt-1'
            >
              <span className='mt-1 mb:mt-0'>
                {typeof count === 'number' ? count : ''}
              </span>
            </Badge>

            <Label className='font-trade-gothic-20 uppercase tracking-wide'>
              {label}
            </Label>
          </Tab>
        );
      })}
    </div>
  </div>
);

HomeTypeSections.propTypes = {
  value: PropTypes.oneOf([HOME_TYPES.QMI, HOME_TYPES.PLAN]).isRequired,
  onChange: PropTypes.func.isRequired,
  counts: PropTypes.objectOf(PropTypes.number),
  isMobile: PropTypes.bool,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
};

export default HomeTypeSections;
